// DirectToolGroup.js — persistent collapsible group of direct tool calls in one agent turn.
// Rendered inline in the message stream. Persisted as role:'tool_group' in DB.
import { ComponentBase } from "../../core/ComponentBase.js";

const TOOL_ICONS = {
    'Shell':            'bi-terminal-fill',
    'Find':             'bi-search',
    'Read':             'bi-file-text',
    'Patch':            'bi-pencil-square',
    'Artifact':         'bi-file-earmark-image',
    'ComputerControl':  'bi-display',
    'ManageVPC':        'bi-server',
    'Present':          'bi-card-list',
    'DelegateToAgent':  'bi-diagram-3',
};

function iconFor(toolName, uiIcon) {
    return uiIcon || TOOL_ICONS[toolName] || 'bi-gear';
}

export class DirectToolGroup extends ComponentBase {
    static styles = `
        .dtg-group { margin: 2px 0; font-size: var(--ab-text-sm); }

        .dtg-header {
            display: flex; align-items: center; gap: 6px;
            padding: 3px 4px; cursor: pointer; border-radius: var(--ab-radius-xs);
            user-select: none; color: var(--bs-secondary-color);
        }
        .dtg-header:hover { background: var(--bs-tertiary-bg); }

        .dtg-chevron { transition: transform 0.18s; font-size: var(--ab-text-2xs); }
        .dtg-group.open .dtg-chevron { transform: rotate(90deg); }

        .dtg-summary { flex: 1; font-size: var(--ab-text-xs); }

        .dtg-status-dot {
            width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
            background: var(--bs-secondary-color);
        }
        .dtg-status-dot.running { background: var(--bs-primary); animation: dtg-pulse 1s infinite; }
        .dtg-status-dot.done    { background: var(--bs-success); }
        .dtg-status-dot.failed  { background: var(--bs-danger); }
        @keyframes dtg-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

        .dtg-body { display: none; padding: 2px 4px 4px 16px; }
        .dtg-group.open .dtg-body { display: block; }

        .dtg-tool {
            display: flex; align-items: flex-start; gap: 6px;
            padding: 2px 0; font-size: var(--ab-text-xs);
            color: var(--bs-secondary-color);
        }
        .dtg-tool.clickable { cursor: pointer; border-radius: var(--ab-radius-xs); padding: 2px 4px; }
        .dtg-tool.clickable:hover { background: var(--bs-tertiary-bg); color: var(--bs-body-color); }
        .dtg-tool-icon { flex-shrink: 0; width: 14px; text-align: center; }
        .dtg-tool-label { flex: 1; }

        .dtg-tool-detail {
            display: none; margin-top: 4px; border-radius: var(--ab-radius-xs); overflow: hidden;
            border: 1px solid var(--bs-border-color-translucent);
        }
        .dtg-tool.expanded .dtg-tool-detail { display: block; }
        .dtg-detail-label {
            font-size: var(--ab-text-2xs); font-weight: 600; text-transform: uppercase;
            letter-spacing: 0.04em; color: var(--bs-secondary-color);
            padding: 2px 8px; background: var(--bs-tertiary-bg);
        }
        .dtg-detail-content {
            padding: 5px 8px; font-family: var(--bs-font-monospace);
            font-size: var(--ab-text-xs); white-space: pre-wrap; word-break: break-all;
            max-height: 180px; overflow-y: auto;
        }
        .spin { animation: dtg-spin 1s linear infinite; display: inline-block; }
        @keyframes dtg-spin { to { transform: rotate(360deg); } }
    `;

    constructor(config) {
        super(config);
        this.tools    = config.tools || [];   // [{ toolName, ui, params, result, status }]
        this.isOpen   = config.isOpen ?? false;  // Collapsed by default
        this.threadId = config.threadId;
        this._toolEls = new Map();
        this.applyStyles();
    }

    render(parentEl) {
        this.container = document.createElement('div');
        this.container.className = 'dtg-group' + (this.isOpen ? ' open' : '');

        this.container.innerHTML = `
            <div class="dtg-header">
                <i class="bi bi-chevron-right dtg-chevron"></i>
                <span class="dtg-status-dot running dtg-dot"></span>
                <span class="dtg-summary">Running…</span>
            </div>
            <div class="dtg-body"></div>
        `;

        this.container.querySelector('.dtg-header').addEventListener('click', () => {
            this.isOpen = !this.isOpen;
            this.container.classList.toggle('open', this.isOpen);
        });

        parentEl.appendChild(this.container);
        this._updateSummary();

        // Render any tools already provided (history reload)
        for (const tool of this.tools) {
            this._renderToolRow(tool);
        }
    }

    addTool(toolData) {
        this.tools.push(toolData);
        this._renderToolRow(toolData);
        this._updateSummary();
    }

    updateTool(toolName, taskId, status, result) {
        const el = this._toolEls.get(taskId);
        if (!el) return;

        // Update the status icon — .dtg-tool-icon is the sole status indicator
        const iconEl = el.querySelector('.dtg-tool-icon');
        if (iconEl) {
            iconEl.innerHTML = status === 'completed'
                ? '<i class="bi bi-check2 text-success"></i>'
                : '<i class="bi bi-x-lg text-danger"></i>';
        }

        // Populate and reveal the output section when result arrives
        const resultContent = el.querySelector('.dtg-result-content');
        if (result && resultContent) {
            const resultStr = typeof result === 'string' ? result : JSON.stringify(result, null, 2);
            resultContent.textContent = resultStr;
            // Reveal the output section (was hidden while running)
            const outputSection = el.querySelector('.dtg-output-section');
            if (outputSection) outputSection.style.display = '';
            // Make the detail block visible if the row is expanded
            const detail = el.querySelector('.dtg-tool-detail');
            if (detail && el.classList.contains('expanded')) detail.style.display = 'block';
        }

        // Update the corresponding tool in this.tools for persistence
        const t = this.tools.find(t => t.taskId === taskId);
        if (t) { t.status = status; t.result = result; }

        this._updateSummary();
    }

    markComplete() {
        const dot = this.container?.querySelector('.dtg-dot');
        if (dot) {
            const anyFailed = this.tools.some(t => t.status === 'failed');
            dot.className = 'dtg-status-dot ' + (anyFailed ? 'failed' : 'done');
        }
        // Collapse after completion — user can expand to see details
        this.isOpen = false;
        this.container?.classList.remove('open');
    }

    _truncateLabel(str) {
        if (!str) return '';
        // Flatten newlines and multiple spaces into a single space
        const singleLine = str.replace(/\s+/g, ' ').trim();
        if (singleLine.length > 80) {
            return singleLine.slice(0, 77) + '...';
        }
        return singleLine;
    }

    _updateSummary() {
        const summaryEl = this.container?.querySelector('.dtg-summary');
        if (!summaryEl) return;
        const n       = this.tools.length;
        const running = this.tools.filter(t => t.status === 'running').length;
        const labels  = [...new Set(this.tools.map(t => {
            const raw = t.ui?.label || t.toolName;
            return this._truncateLabel(raw);
        }))];

        let summaryText = '';
        if (labels.length === 0) {
            summaryText = running > 0 ? 'Running…' : 'Completed';
        } else {
            const rawLabel = labels.length <= 2 ? labels.join(', ') : `${labels[0]} +${labels.length - 1} more`;
            if (running > 0) {
                // Active: if the label already has a progressive verb, use it directly
                if (/^(Running|Navigating|Searching|Reading|Presenting|Updating|Patching)/i.test(rawLabel)) {
                    summaryText = `${rawLabel}…`;
                } else {
                    summaryText = `Using ${rawLabel}…`;
                }
            } else {
                // Completed: elegantly translate active verbs into past tense
                let pastLabel = rawLabel;
                if (/^Running:\s*/i.test(pastLabel)) {
                    pastLabel = pastLabel.replace(/^Running:\s*/i, 'Ran: ');
                } else if (/^Running command:\s*/i.test(pastLabel)) {
                    pastLabel = pastLabel.replace(/^Running command:\s*/i, 'Ran command: ');
                } else if (/^Running\s+/i.test(pastLabel)) {
                    pastLabel = pastLabel.replace(/^Running\s+/i, 'Ran ');
                } else if (/^Navigating\s+/i.test(pastLabel)) {
                    pastLabel = pastLabel.replace(/^Navigating\s+/i, 'Navigated ');
                } else if (/^Searching\s+/i.test(pastLabel)) {
                    pastLabel = pastLabel.replace(/^Searching\s+/i, 'Searched ');
                } else if (/^Reading\s+/i.test(pastLabel)) {
                    pastLabel = pastLabel.replace(/^Reading\s+/i, 'Read ');
                } else if (/^Presenting\s+/i.test(pastLabel)) {
                    pastLabel = pastLabel.replace(/^Presenting\s+/i, 'Presented ');
                } else if (/^Updating\s+/i.test(pastLabel)) {
                    pastLabel = pastLabel.replace(/^Updating\s+/i, 'Updated ');
                } else if (/^Patching\s+/i.test(pastLabel)) {
                    pastLabel = pastLabel.replace(/^Patching\s+/i, 'Patched ');
                } else {
                    pastLabel = `Used ${pastLabel}`;
                }
                summaryText = `${pastLabel} (${n} step${n !== 1 ? 's' : ''})`;
            }
        }
        summaryEl.textContent = summaryText;
    }

    _renderToolRow(toolData) {
        const body = this.container?.querySelector('.dtg-body');
        if (!body) return;

        const { toolName, ui = {}, params, result, status = 'running', taskId } = toolData;
        if (ui.hidden) return;

        const icon  = iconFor(toolName, ui.icon);
        const label = this._truncateLabel(ui.label || toolName);

        const row = document.createElement('div');
        row.className = 'dtg-tool';

        const hasInput  = ui.showInput  && params && Object.keys(params).length > 0;
        // Always render the output container if showOutput is true — result arrives later via updateTool
        const showOutput = ui.showOutput;
        const hasOutput  = showOutput && result;   // true only when result already known (history reload)
        const clickable  = hasInput || showOutput;
        if (clickable) row.classList.add('clickable');

        const statusIcon = status === 'running'
            ? `<i class="bi bi-arrow-repeat spin text-primary"></i>`
            : status === 'completed'
            ? `<i class="bi bi-check2 text-success"></i>`
            : `<i class="bi bi-x-lg text-danger"></i>`;

        const inputStr  = hasInput  ? (ui.inputStr || (ui.formatInput  ? ui.formatInput(params)  : JSON.stringify(params, null, 2))) : '';
        const outputStr = hasOutput ? (ui.outputStr || (ui.formatOutput ? ui.formatOutput(result) : (typeof result === 'string' ? result : JSON.stringify(result, null, 2)))) : '';

        row.innerHTML = `
            <div class="dtg-tool-icon">${statusIcon}</div>
            <div style="flex:1;min-width:0;">
                <div style="display:flex;align-items:center;gap:6px;">
                    <i class="bi ${this.escapeHtml(icon)} text-secondary" style="font-size:var(--ab-text-sm)"></i>
                    <span class="dtg-tool-label">${this.escapeHtml(label)}</span>
                </div>
                <div class="dtg-tool-detail">
                    ${hasInput ? `
                    <div class="dtg-detail-section">
                        <div class="dtg-detail-label">Input</div>
                        <div class="dtg-detail-content">${this.escapeHtml(inputStr)}</div>
                    </div>` : ''}
                    ${showOutput ? `
                    <div class="dtg-detail-section dtg-output-section" style="${hasOutput ? '' : 'display:none'}">
                        <div class="dtg-detail-label">Output</div>
                        <div class="dtg-detail-content dtg-result-content">${hasOutput ? this.escapeHtml(outputStr) : ''}</div>
                    </div>` : ''}
                </div>
            </div>
        `;

        if (clickable) {
            row.addEventListener('click', (e) => {
                e.stopPropagation();
                row.classList.toggle('expanded');
            });
        }

        body.appendChild(row);
        if (taskId) this._toolEls.set(taskId, row);
    }
}
